/**
 * Socket 服务模块
 * 提供 WebSocket 连接的核心功能，包括连接管理、事件处理、认证等
 */

import type { Socket } from 'socket.io-client'
import { io } from 'socket.io-client'
import type { AuthInfo, RoomInfo, ServerResponse } from './config'
import { DEFAULT_OPTIONS, DEFAULT_URL, IS_LOG, LOCAL_STORAGE_TOKEN_KEY, SocketEvent, SocketStatus } from './config'
import { SocketEventManager } from './event'
import type { ConnectionState, EventHandler, SocketError } from './types'

import { useStorage } from '@vueuse/core'
import { computed, watch } from 'vue'

const token = useStorage(LOCAL_STORAGE_TOKEN_KEY, '')

/**
 * Socket 服务类
 * 管理 WebSocket 连接的生命周期和事件处理
 */
export class SocketService {
  // Socket 实例
  private socket: Socket
  // 事件管理器
  private eventManager: SocketEventManager
  // 连接状态
  private status: SocketStatus = SocketStatus.DISCONNECTED
  // 重连尝试次数
  private reconnectAttempts = 0
  // 最大重连尝试次数
  private readonly maxReconnectAttempts = 5
  // 基础重连延迟时间(ms)
  private readonly baseReconnectDelay = 1000
  // 最大重连延迟时间(ms)
  private readonly maxReconnectDelay = 30000
  // 心跳检测定时器
  private heartbeatInterval: NodeJS.Timeout | null = null
  // 连接状态信息
  private connectionState: ConnectionState = {
    status: 'disconnected',
    reconnectAttempts: 0,
    latency: 0,
    lastHeartbeat: 0,
  }

  // 引用计数
  private refCount = 0
  // 清理函数集合
  private cleanupFunctions = new Set<() => void>()

  private token?: string
  private userId?: string
  private reconnectTimer: NodeJS.Timeout | null = null
  private isAuthing = false
  // 添加token监听器引用
  private tokenWatcher: (() => void) | null = null

  /**
   * 获取或创建 Socket 服务实例
   */
  static getInstance(url: string = DEFAULT_URL): SocketService {
    const instance = new SocketService(url)
    return instance
  }

  /**
   * 构造函数
   * @param url WebSocket 服务器地址
   * @param roomId 房间ID
   * @param token 认证令牌
   * @param options Socket 配置选项
   */
  constructor(
    private url: string = DEFAULT_URL,
  ) {
    if (!url) {
      throw new Error('Invalid WebSocket URL')
    }
    else {
      this.url = url
    }

    // 创建原生socket实例
    this.socket = io(this.url, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.baseReconnectDelay,
      reconnectionDelayMax: this.maxReconnectDelay,
      randomizationFactor: 0.5,
      timeout: 20000,
      autoConnect: true, // 自动连接
      forceNew: true, // 强制创建新连接
      closeOnBeforeunload: false, // 阻止页面关闭时断开连接
    })

    // 创建事件管理器
    this.eventManager = new SocketEventManager(this.socket)

    // 立即设置token监听器，不等待连接成功
    this.setupTokenWatcher()

    // 初始化事件监听
    this.socket.on('connect', async () => {
      if (IS_LOG) {
        console.log('Socket connected, starting auth process...')
      }
      try {
        // 如果当前有token，立即进行认证
        if (token.value) {
          this.token = token.value
          await this.doAuth()
        } else {
          // 未登录状态，只进行基础连接
          if (IS_LOG) {
            console.log('No token found, maintaining basic connection')
          }
        }
      }
      catch (error) {
        if (IS_LOG) {
          console.error('Auto auth failed:', error)
        }
      }
      this.onConnect()
    })

    // 阻止主动断开连接
    this.socket.on('disconnect', (reason) => {
      if (reason === 'io client disconnect') {
        this.socket.connect()
        return
      }
      this.onDisconnect(reason)
    })
    this.socket.on('error', error => this.onError(error))
    this.socket.on('reconnect_attempt', attempt => this.onReconnectAttempt(attempt))
    this.socket.on('reconnect_failed', () => this.onReconnectFailed())
    this.socket.on('pong', latency => this.onPong(latency))
    
    // 添加全局认证响应监听器
    this.socket.on(SocketEvent.AUTH, (response: any) => {
      if (IS_LOG) {
        console.log('Global auth response received:', response)
      }
      // 如果当前状态是正在认证，则更新状态
      if (this.status === SocketStatus.CONNECTED && response && (response.success || response.code === 0)) {
        this.status = SocketStatus.AUTHENTICATED
        if (IS_LOG) {
          console.log('Authentication status updated to authenticated')
        }
      }
    })
    
    // 添加其他可能的认证响应事件监听器
    this.socket.on('auth_success', (response: any) => {
      if (IS_LOG) {
        console.log('Auth success event received:', response)
      }
      this.status = SocketStatus.AUTHENTICATED
    })

    this.socket.on('auth_failed', (response: any) => {
      if (IS_LOG) {
        console.log('Auth failed event received:', response)
      }
      this.status = SocketStatus.CONNECTED
    })

    this.startHeartbeat()
  }

  /**
   * 设置token监听器
   */
  private setupTokenWatcher() {
    // 先清除之前的监听器，避免重复
    if (this.tokenWatcher) {
      this.tokenWatcher()
      this.tokenWatcher = null
    }

    // 设置新的token监听器
    this.tokenWatcher = watch(computed(() => token.value), (newVal, oldVal) => {
      if (IS_LOG) {
        console.log('Token changed:', { old: oldVal, new: newVal })
      }
      
      if (newVal && newVal !== oldVal) {
        // token被设置或更新
        this.token = newVal
        if (this.socket.connected) {
          // 如果socket已连接，立即认证
          this.doAuth().catch((error) => {
            if (IS_LOG) {
              console.error('Token change auth failed:', error)
            }
          })
        } else {
          // 如果socket未连接，等待连接成功后认证
          if (IS_LOG) {
            console.log('Socket not connected, waiting for connection to authenticate...')
          }
          const checkConnection = () => {
            if (this.socket.connected) {
              this.doAuth().catch((error) => {
                if (IS_LOG) {
                  console.error('Delayed auth failed:', error)
                }
              })
            } else {
              setTimeout(checkConnection, 100)
            }
          }
          checkConnection()
        }
      } else if (!newVal && oldVal) {
        // token被清除
        if (IS_LOG) {
          console.log('Token cleared, resetting auth status')
        }
        this.token = undefined
        this.status = SocketStatus.CONNECTED
      }
    }, {
      deep: true,
      immediate: true,
    })
  }

  /**
   * 初始化事件监听器
   * 设置各种事件的处理函数
   */

  /**
   * 启动心跳检测
   * 定期发送心跳包以保持连接
   */
  private startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
    }

    this.heartbeatInterval = setInterval(() => {
      if (this.socket.connected) {
        const start = Date.now()
        this.socket.emit('ping', () => {
          this.connectionState.latency = Date.now() - start
          this.connectionState.lastHeartbeat = Date.now()
        })
      }
    }, 60000)
  }

  /**
   * 连接成功处理
   * 更新连接状态和重连计数
   */
  private onConnect() {
    this.status = SocketStatus.CONNECTED
    this.reconnectAttempts = 0
    this.connectionState.status = 'connected'
    this.connectionState.lastError = undefined
    if (IS_LOG) {
      console.log('Socket connected')
    }
  }

  /**
   * 断开连接处理
   * @param reason 断开原因
   */
  private onDisconnect(reason: string) {
    this.status = SocketStatus.DISCONNECTED
    this.connectionState.status = 'disconnected'

    // 判断断开原因
    switch (reason) {
      case 'io client disconnect':
        // 客户端主动断开，不进行重连

        break
      case 'io server disconnect':
        // 服务器主动断开，尝试重连

        this.attemptReconnect()
        break
      case 'transport close':
        // 传输层关闭，尝试重连

        this.attemptReconnect()
        break
      case 'ping timeout':
        // 心跳超时，尝试重连

        this.attemptReconnect()
        break
      default:
        // 其他原因，尝试重连

        this.attemptReconnect()
    }

    if (IS_LOG) {
      console.log('Socket disconnected:', reason)
    }
  }

  /**
   * 尝试重连
   */
  private attemptReconnect() {
    // 清除之前的重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }

    // 设置重连定时器
    this.reconnectTimer = setTimeout(() => {
      if (!this.socket.connected) {
        this.socket.connect()
      }
    }, 1000)
  }

  /**
   * 错误处理
   * @param error 错误对象
   */
  private onError(error: Error) {
    const socketError: SocketError = {
      name: 'SocketError',
      message: error.message,
      code: 500,
      timestamp: Date.now(),
      recoverable: true,
      details: error,
    }

    this.connectionState.lastError = socketError

    if (IS_LOG) {
      console.error('Socket error:', socketError)
    }
  }

  /**
   * 重连尝试处理
   * @param attempt 重连尝试次数
   */
  private onReconnectAttempt(attempt: number) {
    this.reconnectAttempts = attempt
    this.connectionState.status = 'reconnecting'
    this.connectionState.reconnectAttempts = attempt

    // 计算指数退避延迟时间
    const delay = Math.min(
      this.baseReconnectDelay * 2 ** (attempt - 1),
      this.maxReconnectDelay,
    )

    if (IS_LOG) {
      console.log(`Reconnection attempt ${attempt}, next attempt in ${delay}ms`)
    }

    // 清除之前的重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }

    // 设置新的重连定时器
    this.reconnectTimer = setTimeout(() => {
      if (!this.socket.connected) {
        this.socket.connect()
      }
    }, delay)
  }

  /**
   * 重连成功处理
   */
  private onReconnect() {
    this.status = SocketStatus.CONNECTED
    this.connectionState.status = 'connected'
    this.reconnectAttempts = 0
    this.connectionState.reconnectAttempts = 0

    // 重连成功后自动重新认证
    if (this.token) {
      this.doAuth().catch((error) => {
        if (IS_LOG) {
          console.error('Auto re-auth failed:', error)
        }
      })
    }

    if (IS_LOG) {
      console.log('Socket reconnected successfully')
    }
  }

  /**
   * 重连失败处理
   */
  private onReconnectFailed() {
    this.status = SocketStatus.DISCONNECTED
    this.connectionState.status = 'disconnected'

    // 清除重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (IS_LOG) {
      console.error('Reconnection failed after all attempts')
    }
  }

  /**
   * 心跳响应处理
   * @param latency 延迟时间
   */
  private onPong(latency: number) {
    this.connectionState.latency = latency
    this.connectionState.lastHeartbeat = Date.now()
  }

  /**
   * 设置用户ID（用于未登录状态）
   * @param userId 用户ID
   */
  setUserId(userId: string) {
    this.userId = userId
    if (IS_LOG) {
      console.log('User ID set:', userId)
    }
  }

  /**
   * 执行认证
   * @returns 认证响应
   */
  public doAuth(): Promise<ServerResponse<AuthInfo>> {
    if (this.isAuthing) {
      if (IS_LOG) console.log('Auth already in progress, skip duplicate');
      return Promise.resolve({ code: 0, success: true, data: { token: this.token } });
    }
    this.isAuthing = true;

    // 从localStorage重新获取token，确保获取最新值
    const currentToken = localStorage.getItem('access_token')
    if (currentToken) {
      this.token = currentToken
    }

    if (!this.socket.connected) {
      if (IS_LOG) {
        console.log('Socket not connected, attempting to connect first...')
      }
      // 如果socket未连接，先尝试连接
      this.socket.connect()
      return new Promise((resolve, reject) => {
        const checkConnection = () => {
          if (this.socket.connected) {
            this.doAuth().then(resolve).catch(reject)
          } else {
            setTimeout(checkConnection, 100)
          }
        }
        checkConnection()
      }).finally(() => {
        this.isAuthing = false
      })
    }

    // 如果没有token，但有用户ID，发送用户ID进行基础连接
    if (!this.token && this.userId) {
      if (IS_LOG) {
        console.log('No token provided, but user ID available, sending user ID for basic connection')
      }
      this.socket.emit('connect_user', { userId: this.userId })
      this.status = SocketStatus.CONNECTED
      return Promise.resolve({
        code: 0,
        success: true,
        data: { userId: this.userId }
      } as ServerResponse<AuthInfo>)
    }

    // 如果既没有token也没有用户ID，只进行基础连接
    if (!this.token && !this.userId) {
      if (IS_LOG) {
        console.log('No token or user ID provided, basic connection only')
      }
      this.status = SocketStatus.CONNECTED
      return Promise.resolve({
        code: 0,
        success: true,
        data: { }
      } as ServerResponse<AuthInfo>)
    }

    // 有token，进行完整认证
    return new Promise((resolve, reject) => {
      if (IS_LOG) {
        console.log('Starting auth process with token:', this.token)
      }
      
      // 设置超时
      const timeout = setTimeout(() => {
        if (IS_LOG) {
          console.log('Auth timeout')
        }
        reject(new Error('Authentication timeout'))
      }, 10000)

      // 发送认证请求
      if (IS_LOG) {
        console.log('Sending auth request with token:', this.token)
      }

      // 发送认证请求
      this.socket.emit(SocketEvent.AUTH, { token: this.token })
      
      // 由于全局监听器会处理响应，我们直接返回成功
      setTimeout(() => {
        clearTimeout(timeout)
        if (this.status === SocketStatus.AUTHENTICATED) {
          resolve({
            code: 0,
            success: true,
            data: { token: this.token }
          } as ServerResponse<AuthInfo>)
        } else {
          reject(new Error('Authentication failed'))
        }
      }, 1000) // 等待1秒让全局监听器处理
    }).finally(() => {
      this.isAuthing = false;
    })
  }

  /**
   * 发送消息
   * @param message 消息内容
   * @returns 发送消息响应
   */
  sendMessage(message: string): Promise<ServerResponse<void>> {
    return new Promise((resolve, reject) => {
      this.socket.emit(SocketEvent.MESSAGE, {

        message,
      }, (response: ServerResponse<void>) => {
        if (response.success) {
          resolve(response)
        }
        else {
          reject(new Error('Send message failed'))
        }
      })
    })
  }

  /**
   * 获取连接状态
   * @returns 连接状态信息
   */
  getConnectionState(): ConnectionState {
    return { ...this.connectionState }
  }

  /**
   * 获取当前状态
   * @returns Socket 状态
   */
  getStatus(): SocketStatus {
    return this.status
  }

  /**
   * 检查是否已认证
   * @returns 是否已认证
   */
  isAuthenticated(): boolean {
    return this.status === SocketStatus.AUTHENTICATED
  }

  /**
   * 检查是否已连接
   * @returns 是否已连接
   */
  isConnected(): boolean {
    return this.socket.connected
  }

  /**
   * 重新连接
   */
  reconnect() {
    // 直接重连，不先断开
    this.socket.connect()
  }

  /**
   * 断开连接
   */
  disconnect() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
    // 不主动断开连接
    // this.socket.disconnect()
  }

  /**
   * 注册事件监听器
   * @param event 事件名称
   * @param handler 事件处理函数
   */
  on<T = any>(event: string, handler: (data: T) => void) {
    // 同时注册到原生 socket 和 eventManager
    this.socket.on(event, handler)
    return this.eventManager.on(event, handler)
  }

  /**
   * 注册一次性事件监听器
   * @param event 事件名称
   * @param handler 事件处理函数
   */
  once<T = any>(event: string, handler: (data: T) => void) {
    // 同时注册到原生 socket 和 eventManager
    this.socket.once(event, handler)
    return this.eventManager.once(event, handler)
  }

  /**
   * 发送事件
   * @param event 事件名称
   * @param data 事件数据
   * @returns Promise
   */
  async emit<T = any>(event: string, data: any): Promise<T> {
    return new Promise((resolve, reject) => {
      this.socket.emit(event, data, (response: T) => {
        resolve(response)
      })
    })
  }

  /**
   * 等待事件
   * @param event 事件名称
   * @param callback 回调函数
   * @returns Promise
   */
  waitFor<T = any>(event: string, callback?: (data: T) => void): Promise<T> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.off(event, handler)
        reject(new Error(`Timeout waiting for event: ${event}`))
      }, 30000)

      const handler = (data: T) => {
        clearTimeout(timeout)
        this.off(event, handler)
        if (callback) {
          callback(data)
        }
        resolve(data)
      }
      this.on(event, handler)
    })
  }

  /**
   * 清理资源
   */
  destroy() {
    // 清除token监听器
    if (this.tokenWatcher) {
      this.tokenWatcher()
      this.tokenWatcher = null
    }
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
    // 不主动断开连接
    // this.socket.disconnect()
    
    this.eventManager.clear()
    this.cleanupFunctions.forEach(cleanup => cleanup())
    this.cleanupFunctions.clear()
    this.refCount = 0
    this.connectionState = {
      status: 'disconnected',
      reconnectAttempts: 0,
      latency: 0,
      lastHeartbeat: 0,
    }

    // 清除重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  /**
   * 获取认证令牌
   * @returns 认证令牌
   */
  getToken(): string | undefined {
    return this.token
  }

  /**
   * 获取Socket实例
   * @returns Socket实例
   */
  getSocket(): Socket {
    return this.socket
  }

  /**
   * 移除事件监听器
   * @param event 事件名称
   * @param handler 事件处理函数
   */
  off(event: string, handler?: EventHandler) {
    if (handler) {
      this.socket.off(event, handler)
      this.eventManager.off(event, handler)
    }
    else {
      this.socket.removeAllListeners(event)
      // 清空 eventManager 中该事件的所有处理器
      const handlers = this.eventManager.eventMap[event] || []
      handlers.forEach(({ handler }) => {
        this.eventManager.off(event, handler)
      })
    }
  }
}
